import { ArrowLeftLine } from '@archipro-design/icons';
import { useUserLoggedIn } from '~/modules/user/hook/use-user';
import { StepHeading } from './StepText';

interface StepAccountConfirmProps {
    /** Copy around the account email, from the variant config's step7 block. */
    confirmationPrefix: string;
    confirmationSuffix: string;
    onContinue: () => void;
    onBack: () => void;
    submitting: boolean;
    errorMessage: string | null;
}

/**
 * The account step for a signed-in user: confirm which account the brief goes
 * out under, then submit it explicitly. Guests never see this — the auth gate
 * segment owns their account step and auto-submits — but a gate journey whose
 * send failed lands back here for the inline retry.
 */
export const StepAccountConfirm = ({
    confirmationPrefix,
    confirmationSuffix,
    onContinue,
    onBack,
    submitting,
    errorMessage,
}: StepAccountConfirmProps) => {
    const userLogin = useUserLoggedIn();
    const firstName = userLogin.isLoggedIn
        ? (userLogin.user.FirstName ?? '')
        : '';
    const accountEmail = userLogin.isLoggedIn
        ? (userLogin.user.Email ?? '')
        : '';

    return (
        <div className="flex flex-col gap-6">
            <button
                type="button"
                aria-label="Back"
                onClick={onBack}
                disabled={submitting}
                className="-mb-2 flex size-8 items-center justify-center self-start rounded-full text-[#181818] hover:bg-black/5 disabled:opacity-60"
            >
                <ArrowLeftLine className="size-5" />
            </button>
            <StepHeading>
                {firstName
                    ? `You're logged in as ${firstName}`
                    : `You're logged in`}
            </StepHeading>
            <p className="text-[1.0625rem] leading-[1.35] tracking-[-0.02em] text-[#444444]">
                {confirmationPrefix}
                <strong>{accountEmail}</strong>
                {confirmationSuffix}
            </p>
            {errorMessage && (
                <span className="text-[0.875rem] text-red-600">
                    {errorMessage}
                </span>
            )}
            <div className="pt-2">
                <button
                    type="button"
                    onClick={onContinue}
                    disabled={submitting}
                    className="flex h-14 w-full items-center justify-center rounded-full bg-[#181818] text-[1.25rem] font-medium tracking-[-0.02em] text-[#f3f3f3] shadow-[0_0.1875rem_0.8125rem_0_rgba(0,0,0,0.25),0_1.25rem_2.125rem_0_rgba(0,0,0,0.25)] transition-colors hover:bg-[#2c2c2c] disabled:opacity-60"
                >
                    {submitting ? 'Sending…' : 'Get Matched'}
                </button>
            </div>
        </div>
    );
};
